import type { WizardFormData } from '../../../../types/wizard';
import { useTranslation } from 'react-i18next';
import StepCard from '../../../../components/wizard/StepCard';
import SelectableChip from '../../../../components/wizard/SelectableChip';

interface Step1ProfileProps {
  formData: WizardFormData;
  updateFormData: (data: Partial<WizardFormData>) => void;
  errors: Record<string, string>;
}

const Step1Profile = ({ formData, updateFormData, errors }: Step1ProfileProps) => {
  const { i18n } = useTranslation();
  const isEnglish = i18n.resolvedLanguage?.startsWith('en');
  const ageRanges = [
    { value: '18-25', label: isEnglish ? '18-25 years' : '18-25 anos' },
    { value: '26-35', label: isEnglish ? '26-35 years' : '26-35 anos' },
    { value: '36-45', label: isEnglish ? '36-45 years' : '36-45 anos' },
    { value: '46-55', label: isEnglish ? '46-55 years' : '46-55 anos' },
    { value: '56-65', label: isEnglish ? '56-65 years' : '56-65 anos' },
    { value: '65+', label: isEnglish ? '65+ years' : '65+ anos' },
  ];

  const occupations = [
    { value: 'estudante', label: isEnglish ? 'Student' : 'Estudante', icon: 'ri-book-line' },
    { value: 'profissional', label: isEnglish ? 'Professional' : 'Profissional', icon: 'ri-briefcase-line' },
    { value: 'empresario', label: isEnglish ? 'Business Owner' : 'Empresário', icon: 'ri-building-line' },
    { value: 'aposentado', label: isEnglish ? 'Retired' : 'Aposentado', icon: 'ri-user-smile-line' },
    { value: 'autonomo', label: isEnglish ? 'Self-employed' : 'Autônomo', icon: 'ri-user-star-line' },
  ];

  const lifestyles = [
    {
      value: 'ativo',
      label: isEnglish ? 'Active' : 'Ativo',
      description: isEnglish ? 'I enjoy movement and physical activities' : 'Gosto de movimento e atividades físicas',
    },
    {
      value: 'equilibrado',
      label: isEnglish ? 'Balanced' : 'Equilibrado',
      description: isEnglish ? 'A balance between action and rest' : 'Balanço entre ação e descanso',
    },
    {
      value: 'contemplativo',
      label: isEnglish ? 'Reflective' : 'Contemplativo',
      description: isEnglish ? 'I prefer observing, slowing down and relaxing' : 'Prefiro observar e relaxar',
    },
  ];

  return (
    <div className="space-y-8">
      {/* Faixa Etária */}
      <StepCard>
        <div className="mb-4">
          <label className="block text-white text-lg font-medium mb-2">
            {isEnglish ? 'What is your age range?' : 'Qual sua faixa etária?'} <span className="text-[#FFD700]">*</span>
          </label>
          <p className="text-white/90 text-sm mb-4">
            {isEnglish
              ? 'This helps us suggest activities that better fit your profile'
              : 'Isso nos ajuda a sugerir atividades adequadas ao seu perfil'}
          </p>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          {ageRanges.map((age) => (
            <SelectableChip
              key={age.value}
              label={age.label}
              selected={formData.step1_profile.ageRange === age.value}
              onClick={() =>
                updateFormData({
                  step1_profile: { ...formData.step1_profile, ageRange: age.value as any },
                })
              }
            />
          ))}
        </div>
        {errors.ageRange && (
          <p className="text-red-400 text-sm mt-2 flex items-center gap-2">
            <i className="ri-error-warning-line"></i>
            {errors.ageRange}
          </p>
        )}
      </StepCard>

      {/* Ocupação */}
      <StepCard>
        <div className="mb-4">
          <label className="block text-white text-lg font-medium mb-2">
            {isEnglish ? 'What is your current occupation?' : 'Qual sua ocupação atual?'} <span className="text-[#FFD700]">*</span>
          </label>
          <p className="text-white/90 text-sm mb-4">
            {isEnglish
              ? 'Understanding your life rhythm helps us shape the right itinerary'
              : 'Entender seu ritmo de vida nos ajuda a criar o roteiro ideal'}
          </p>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          {occupations.map((occ) => (
            <SelectableChip
              key={occ.value}
              label={occ.label}
              icon={occ.icon}
              selected={formData.step1_profile.occupation === occ.value}
              onClick={() =>
                updateFormData({
                  step1_profile: { ...formData.step1_profile, occupation: occ.value as any },
                })
              }
            />
          ))}
        </div>
        {errors.occupation && (
          <p className="text-red-400 text-sm mt-2 flex items-center gap-2">
            <i className="ri-error-warning-line"></i>
            {errors.occupation}
          </p>
        )}
      </StepCard>

      {/* Estilo de Vida */}
      <StepCard>
        <div className="mb-4">
          <label className="block text-white text-lg font-medium mb-2">
            {isEnglish ? 'How would you describe your lifestyle?' : 'Como você descreveria seu estilo de vida?'} <span className="text-[#FFD700]">*</span>
          </label>
          <p className="text-white/90 text-sm mb-4">
            {isEnglish
              ? 'This helps define the rhythm and intensity of the experiences'
              : 'Isso define o ritmo e intensidade das experiências'}
          </p>
        </div>
        <div className="space-y-3">
          {lifestyles.map((lifestyle) => (
            <button
              key={lifestyle.value}
              type="button"
              onClick={() =>
                updateFormData({
                  step1_profile: { ...formData.step1_profile, lifestyle: lifestyle.value as any },
                })
              }
              className={`w-full p-4 rounded-xl border-2 transition-all duration-200 text-left ${
                formData.step1_profile.lifestyle === lifestyle.value
                  ? 'border-[#FFD700] bg-[#FFD700]/10'
                  : 'border-white/10 bg-white/5 hover:border-white/20'
              }`}
            >
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-white font-medium text-base mb-1">{lifestyle.label}</p>
                  <p className="text-white/90 text-sm">{lifestyle.description}</p>
                </div>
                {formData.step1_profile.lifestyle === lifestyle.value && (
                  <i className="ri-checkbox-circle-fill text-[#FFD700] text-xl"></i>
                )}
              </div>
            </button>
          ))}
        </div>
        {errors.lifestyle && (
          <p className="text-red-400 text-sm mt-2 flex items-center gap-2">
            <i className="ri-error-warning-line"></i>
            {errors.lifestyle}
          </p>
        )}
      </StepCard>
    </div>
  );
};

export default Step1Profile;
